/**
 * KnowledgeBrowser — three-pane KB explorer.
 *
 * Layout:
 *   ┌─ Toolbar: KB selector | + New KB | + Mkdir | upload picker ─┐
 *   │ ┌── FS tree ───────────┬── Preview pane ────────────────┐  │
 *   │ │ raw/                 │  metadata strip                 │  │
 *   │ │   docs/              │  ──────────────────────────     │  │
 *   │ │     report.pdf       │  text / image / pdf preview     │  │
 *   │ │   imgs/              │                                 │  │
 *   │ │ processed/           │                                 │  │
 *   │ │ wiki/                │                                 │  │
 *   │ └──────────────────────┴────────────────────────────────┘  │
 *   └────────────────────────────────────────────────────────────┘
 *
 * KB shape (kos_knowledge.storage.keys):
 *   - `raw/`       — user-uploaded source documents. **Only writable section.**
 *   - `processed/` — OCR/embedding artefacts produced by the worker.
 *   - `wiki/`      — generated wiki pages with citations.
 *
 * Mirrors lemon_agent's `/kb` page but talks to the SaaS gateway (so
 * everything is tenant-scoped). KB list comes seeded from the RSC; all
 * mutations refetch the affected slice rather than triggering a full
 * RSC round trip — keeps interactions snappy.
 *
 * Right-pane tabs:
 *   - **Preview** — file content (auto-selected when a file is clicked).
 *   - **Documents** — ingestion status table + per-doc reembed button,
 *     backed by `/v1/kbs/{id}/documents`.
 *   - **Search** — query box + ranked snippets from
 *     `/v1/kbs/{id}/search`. Clicking a hit opens the source file in
 *     the Preview tab.
 *
 * NOT YET WIRED (next milestone):
 *   - inline text editing (PUT /fs/file)
 *   - move / rename UX
 *   - KB-level edit (rename, MCP toggles)
 */

const KB_SECTIONS = ["raw", "processed", "wiki"] as const;
type KbSection = (typeof KB_SECTIONS)[number];

const SECTION_HINT: Record<KbSection, string> = {
  raw: "Source documents you upload",
  processed: "OCR + embedding artefacts (read-only)",
  wiki: "Generated wiki pages (read-only)",
};

function topSection(path: string): KbSection | null {
  const top = path.split("/", 1)[0] as KbSection;
  return (KB_SECTIONS as readonly string[]).includes(top) ? top : null;
}

import {
  ChevronDown,
  ChevronRight,
  File,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import {
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ExtractionTab } from "@/features/knowledge/extraction-tab";
import { TaxonomyTab } from "@/features/knowledge/taxonomy-tab";
import { api, apiUpload, apiUrl } from "@/lib/api";
import type {
  CreateTeamKbRequest,
  DocumentStatus,
  KbCatBytesResponse,
  KbDocumentListResponse,
  KbDocumentView,
  KbFileEntry,
  KbHeadResponse,
  KbReembedResponse,
  KbReextractResponse,
  KbSearchHit,
  KbSearchRequest,
  KbSearchResponse,
  KbUploadResponse,
  KbView,
} from "@/lib/types";

interface KnowledgeBrowserProps {
  initial: { kbs: KbView[] };
  isAdmin: boolean;
}

export function KnowledgeBrowser({ initial, isAdmin }: KnowledgeBrowserProps) {
  const [kbs, setKbs] = useState<KbView[]>(initial.kbs);
  const [selectedKbId, setSelectedKbId] = useState<string | null>(
    initial.kbs.length > 0 ? initial.kbs[0].id : null,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [mkdirOpen, setMkdirOpen] = useState(false);
  // FS state — `raw` is open by default since that's the only section
  // users can write to. The other two open on demand.
  const [openDirs, setOpenDirs] = useState<Set<string>>(new Set(["raw"]));
  const [entriesByDir, setEntriesByDir] = useState<Record<string, KbFileEntry[] | "loading">>({});
  const [entriesError, setEntriesError] = useState<string | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  // Right-pane tabs. We keep "preview" as the implicit default so a
  // file click feels instant. When the user switches to documents /
  // search, we don't tear down the preview state — they can flip back.
  const [rightTab, setRightTab] = useState<
    "preview" | "documents" | "search" | "taxonomy" | "extraction"
  >("preview");
  // Bumped after a successful candidate promote / reject so the
  // taxonomy tab refreshes the next time it's visible. Sibling tabs
  // invalidate the catalog by calling ``setTaxonomyRefreshKey``
  // rather than threading state top-down — the tab re-runs its
  // loader on ``refreshKey`` change inside ``TaxonomyTab``.
  const [taxonomyRefreshKey, setTaxonomyRefreshKey] = useState(0);
  // Bumped on doc upload + after candidate promotes so the
  // ExtractionTab refreshes its candidate queues and (when polling
  // for run rows) restarts.
  const [extractionRefreshKey, setExtractionRefreshKey] = useState(0);
  // Bumped after a successful upload so the documents pane refetches
  // (instead of polling). Indirect refresh keeps the panes loosely
  // coupled — the documents pane owns its own loading state.
  const [docsRefreshKey, setDocsRefreshKey] = useState(0);

  const selectedKb = useMemo(
    () => kbs.find((k) => k.id === selectedKbId) ?? null,
    [kbs, selectedKbId],
  );

  // Reset directory state whenever the active KB changes — entries for
  // KB A are not valid in KB B's tree. selectedKbId is intentionally
  // *read* (not closed-over) inside the effect, so biome thinks it's
  // unused; we touch it to satisfy the dependency check.
  useEffect(() => {
    void selectedKbId;
    setOpenDirs(new Set(["raw"]));
    setEntriesByDir({});
    setSelectedPath(null);
    setEntriesError(null);
    setRightTab("preview");
    setDocsRefreshKey((k) => k + 1);
  }, [selectedKbId]);

  // Fetch a directory listing. The knowledge-service refuses paths that
  // don't begin with raw/processed/wiki — for the synthetic root we just
  // synthesise the three sections instead of going to the network.
  const refreshDir = useCallback(async (kbId: string, dir: string) => {
    if (dir === "") {
      const synth: KbFileEntry[] = KB_SECTIONS.map((s) => ({
        name: s,
        path: s,
        kind: "dir",
        size_bytes: 0,
        mime_type: null,
        content_hash: null,
        updated_at: new Date(0).toISOString(),
      }));
      setEntriesByDir((prev) => ({ ...prev, "": synth }));
      setEntriesError(null);
      return;
    }
    setEntriesByDir((prev) => ({ ...prev, [dir]: "loading" }));
    try {
      const params = new URLSearchParams({ path: dir });
      const rows = await api<KbFileEntry[]>(`/kbs/${kbId}/fs/ls?${params.toString()}`);
      setEntriesByDir((prev) => ({ ...prev, [dir]: rows }));
      setEntriesError(null);
    } catch (err) {
      setEntriesByDir((prev) => {
        const next = { ...prev };
        delete next[dir];
        return next;
      });
      setEntriesError(err instanceof Error ? err.message : "Failed to list directory");
    }
  }, []);

  // When a KB becomes active, synth the root and prefetch raw/.
  useEffect(() => {
    if (!selectedKbId) return;
    void refreshDir(selectedKbId, "");
    void refreshDir(selectedKbId, "raw");
  }, [selectedKbId, refreshDir]);

  const handleToggleDir = useCallback(
    (dir: string): void => {
      setOpenDirs((prev) => {
        const next = new Set(prev);
        if (next.has(dir)) {
          next.delete(dir);
        } else {
          next.add(dir);
          if (selectedKbId && entriesByDir[dir] === undefined) {
            void refreshDir(selectedKbId, dir);
          }
        }
        return next;
      });
    },
    [selectedKbId, entriesByDir, refreshDir],
  );

  const handleSelectFile = useCallback((path: string): void => {
    setSelectedPath(path);
    setRightTab("preview");
  }, []);

  // Refresh the parent directory of `path` — used after every mutation so
  // the tree updates without a full reload.
  const refreshParentOf = useCallback(
    async (path: string) => {
      if (!selectedKbId) return;
      const idx = path.lastIndexOf("/");
      const parent = idx === -1 ? "" : path.slice(0, idx);
      await refreshDir(selectedKbId, parent);
    },
    [selectedKbId, refreshDir],
  );

  async function handleCreatedKb(kb: KbView) {
    setKbs((prev) => [...prev, kb]);
    setSelectedKbId(kb.id);
    setCreateOpen(false);
    toast.success(`Created KB "${kb.name}"`);
  }

  async function handleMkdir(path: string) {
    if (!selectedKb) return;
    if (topSection(path) !== "raw") {
      toast.error("New directories must live under raw/");
      return;
    }
    try {
      await api(`/kbs/${selectedKb.id}/fs/mkdir`, {
        method: "POST",
        body: JSON.stringify({ path }),
      });
      toast.success(`Created /${path}`);
      setMkdirOpen(false);
      // Open + refresh the new dir so the user can see it.
      const idx = path.lastIndexOf("/");
      const parent = idx === -1 ? "" : path.slice(0, idx);
      await refreshDir(selectedKb.id, parent);
      setOpenDirs((prev) => new Set([...prev, parent, path]));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create directory");
    }
  }

  async function handleDelete(entry: KbFileEntry) {
    if (!selectedKb) return;
    // The 3 fixed sections are virtual roots — cannot be deleted.
    if (entry.kind === "dir" && (KB_SECTIONS as readonly string[]).includes(entry.path)) {
      toast.error(`${entry.path}/ is a built-in section and cannot be deleted`);
      return;
    }
    // Non-raw sections are read-only (worker writes them).
    if (topSection(entry.path) !== "raw") {
      toast.error(`${topSection(entry.path)}/ is read-only`);
      return;
    }
    if (
      !confirm(
        `Delete ${entry.kind === "dir" ? "directory" : "file"} "${entry.path}"? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      const params = new URLSearchParams({
        path: entry.path,
        recursive: entry.kind === "dir" ? "true" : "false",
      });
      await api(`/kbs/${selectedKb.id}/fs?${params.toString()}`, { method: "DELETE" });
      toast.success(`Deleted ${entry.path}`);
      if (selectedPath === entry.path || selectedPath?.startsWith(`${entry.path}/`)) {
        setSelectedPath(null);
      }
      await refreshParentOf(entry.path);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    }
  }

  return (
    <div className="space-y-3">
      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={selectedKbId ?? ""} onValueChange={setSelectedKbId}>
          <SelectTrigger className="w-[280px]">
            <SelectValue placeholder="Select a knowledge base" />
          </SelectTrigger>
          <SelectContent>
            {kbs.map((kb) => (
              <SelectItem key={kb.id} value={kb.id}>
                <span className="flex items-center gap-2">
                  <span className="font-mono text-xs">{kb.slug}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {kb.scope}
                  </Badge>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isAdmin ? (
          <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
            <Plus /> New KB
          </Button>
        ) : null}
        {selectedKb ? (
          <>
            <UploadButton
              kbId={selectedKb.id}
              onUploaded={async (file) => {
                await refreshParentOf(file.path);
                setOpenDirs((prev) => {
                  const next = new Set(prev);
                  const idx = file.path.lastIndexOf("/");
                  if (idx > 0) next.add(file.path.slice(0, idx));
                  next.add("raw");
                  return next;
                });
                setSelectedPath(file.path);
                // The freshly uploaded file kicked an ingest job; nudge
                // the documents pane so its status badge appears as
                // "pending" without the user clicking refresh.
                setDocsRefreshKey((k) => k + 1);
              }}
            />
            <Button size="sm" variant="outline" onClick={() => setMkdirOpen(true)}>
              <FolderPlus /> Mkdir
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (!selectedKbId) return;
                for (const section of KB_SECTIONS) {
                  if (openDirs.has(section)) {
                    void refreshDir(selectedKbId, section);
                  }
                }
              }}
              aria-label="Refresh tree"
            >
              <RefreshCw />
            </Button>
          </>
        ) : null}
        {selectedKb ? (
          <span className="ml-auto text-xs text-muted-foreground">
            <span className="font-mono">{selectedKb.scope}</span>
            {" · "}
            <span className="font-mono">/{selectedKb.slug}</span>
            {selectedKb.description ? ` — ${selectedKb.description}` : null}
          </span>
        ) : null}
      </div>

      {selectedKb ? <EmbeddingConfigStrip kb={selectedKb} /> : null}

      {/* ── Three-pane body ─────────────────────────────────── */}
      {kbs.length === 0 ? (
        <EmptyState>
          No knowledge bases yet. {isAdmin ? "Create one to get started." : null}
        </EmptyState>
      ) : selectedKb ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[320px_1fr]">
          {/* Tree */}
          <div className="rounded-lg border border-border bg-card/50 p-2">
            {entriesError ? <ErrorBanner>{entriesError}</ErrorBanner> : null}
            <DirNode
              kbId={selectedKb.id}
              dir=""
              displayName="(root)"
              openDirs={openDirs}
              entriesByDir={entriesByDir}
              onToggle={handleToggleDir}
              onSelectFile={handleSelectFile}
              onDelete={handleDelete}
              selectedPath={selectedPath}
              depth={0}
            />
          </div>
          {/* Right pane — Preview / Documents / Search tabs */}
          <div className="flex flex-col rounded-lg border border-border bg-card/50">
            <Tabs
              value={rightTab}
              onValueChange={(v) => setRightTab(v as typeof rightTab)}
              className="flex flex-1 flex-col"
            >
              <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                <TabsList className="h-7 p-0.5">
                  <TabsTrigger value="preview" className="h-6 px-3">
                    <FileText className="h-3 w-3" />
                    Preview
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="h-6 px-3">
                    <Folder className="h-3 w-3" />
                    Documents
                  </TabsTrigger>
                  <TabsTrigger value="search" className="h-6 px-3">
                    <Search className="h-3 w-3" />
                    Search
                  </TabsTrigger>
                  <TabsTrigger value="taxonomy" className="h-6 px-3">
                    <Sparkles className="h-3 w-3" />
                    Taxonomy
                  </TabsTrigger>
                  <TabsTrigger value="extraction" className="h-6 px-3">
                    <Sparkles className="h-3 w-3" />
                    Extraction
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="preview" className="mt-0 min-h-0 flex-1 overflow-hidden">
                {selectedPath ? (
                  <FilePreview kbId={selectedKb.id} path={selectedPath} />
                ) : (
                  <EmptyState className="m-3">
                    Pick a file in the tree to preview it. Drag a file onto the upload button — or
                    use Mkdir — to add new content.
                  </EmptyState>
                )}
              </TabsContent>
              <TabsContent value="documents" className="mt-0 min-h-0 flex-1 overflow-hidden">
                <DocumentsPane
                  kbId={selectedKb.id}
                  refreshKey={docsRefreshKey}
                  onOpenSource={(uri) => {
                    // ``source_uri`` is an ``s3://bucket/team/<team_id>/kb/<kb_id>/<rel-key>``
                    // shaped URL. The KB browser only knows logical paths
                    // (raw/foo.md) so we extract the trailing path
                    // component the user uploaded. If the heuristic fails,
                    // we just bounce them back to the file tree on raw/.
                    const match = uri.match(/\/raw\/(.+)$/);
                    if (match) {
                      const path = `raw/${match[1]}`;
                      setSelectedPath(path);
                      setRightTab("preview");
                    } else {
                      toast.info(`Source URI: ${uri}`);
                    }
                  }}
                  onExtractionTriggered={() => setExtractionRefreshKey((k) => k + 1)}
                />
              </TabsContent>
              <TabsContent value="search" className="mt-0 min-h-0 flex-1 overflow-hidden">
                <SearchPane
                  kbId={selectedKb.id}
                  onOpenHit={(hit) => {
                    const match = hit.source_uri.match(/\/raw\/(.+)$/);
                    if (match) {
                      const path = `raw/${match[1]}`;
                      setSelectedPath(path);
                      setRightTab("preview");
                    }
                  }}
                />
              </TabsContent>
              <TabsContent value="taxonomy" className="mt-0 min-h-0 flex-1 overflow-hidden">
                <TaxonomyTab
                  kbId={selectedKb.id}
                  refreshKey={taxonomyRefreshKey}
                  onCatalogChanged={() => setExtractionRefreshKey((k) => k + 1)}
                />
              </TabsContent>
              <TabsContent value="extraction" className="mt-0 min-h-0 flex-1 overflow-hidden">
                <ExtractionTab
                  kbId={selectedKb.id}
                  refreshKey={extractionRefreshKey}
                  onPromoted={() => {
                    // A promoted candidate produced a new catalog row;
                    // bump both refresh keys so the Taxonomy tab and
                    // any other in-tab queries see it.
                    setTaxonomyRefreshKey((k) => k + 1);
                    setExtractionRefreshKey((k) => k + 1);
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      ) : null}

      {createOpen ? (
        <CreateKbDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={handleCreatedKb}
        />
      ) : null}
      {mkdirOpen ? (
        <MkdirDialog open={mkdirOpen} onOpenChange={setMkdirOpen} onSubmit={handleMkdir} />
      ) : null}
    </div>
  );
}

function EmbeddingConfigStrip({ kb }: { kb: KbView }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      <Sparkles className="h-3.5 w-3.5 text-primary" />
      <span className="font-medium text-foreground">Embedding</span>
      <Badge variant="outline" className="font-mono text-[10px]">
        {kb.embedding.provider}
      </Badge>
      <span className="font-mono text-foreground">{kb.embedding.model}</span>
      <span>{kb.embedding.dimensions} dim</span>
      <span>batch {kb.embedding.batch_size}</span>
      <span>dimensions param {kb.embedding.send_dimensions ? "on" : "off"}</span>
      {kb.embedding.base_url ? (
        <>
          <span className="hidden text-border sm:inline">|</span>
          <span className="truncate font-mono" title={kb.embedding.base_url}>
            {kb.embedding.base_url}
          </span>
        </>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// FS tree
// ────────────────────────────────────────────────────────────────────

interface DirNodeProps {
  kbId: string;
  dir: string;
  displayName: string;
  openDirs: Set<string>;
  entriesByDir: Record<string, KbFileEntry[] | "loading">;
  onToggle: (dir: string) => void;
  onSelectFile: (path: string) => void;
  onDelete: (entry: KbFileEntry) => void;
  selectedPath: string | null;
  depth: number;
}

function DirNode({
  kbId,
  dir,
  displayName,
  openDirs,
  entriesByDir,
  onToggle,
  onSelectFile,
  onDelete,
  selectedPath,
  depth,
}: DirNodeProps) {
  const isOpen = openDirs.has(dir);
  const entries = entriesByDir[dir];

  // Root dir is always rendered without a chevron row — its contents fill the panel.
  if (dir === "" && depth === 0) {
    return (
      <div className="space-y-0.5">
        {entries === undefined || entries === "loading" ? (
          <TreeSkeleton />
        ) : entries.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">(empty)</p>
        ) : (
          entries.map((e) => (
            <EntryRow
              key={e.path}
              kbId={kbId}
              entry={e}
              openDirs={openDirs}
              entriesByDir={entriesByDir}
              onToggle={onToggle}
              onSelectFile={onSelectFile}
              onDelete={onDelete}
              selectedPath={selectedPath}
              depth={depth + 1}
            />
          ))
        )}
      </div>
    );
  }

  const section = topSection(dir);
  const isSectionRoot = section !== null && dir === section;
  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-1 rounded px-1.5 py-0.5 text-left text-xs hover:bg-muted"
        style={{ paddingLeft: `${depth * 12 + 6}px` }}
        onClick={() => onToggle(dir)}
        title={isSectionRoot && section ? SECTION_HINT[section] : undefined}
      >
        {isOpen ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground" />
        )}
        {isOpen ? (
          <FolderOpen className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <Folder className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <span className="font-mono">{displayName}</span>
        {isSectionRoot && section !== "raw" ? (
          <Badge variant="outline" className="ml-1 text-[10px]">
            read-only
          </Badge>
        ) : null}
      </button>
      {isOpen ? (
        entries === undefined || entries === "loading" ? (
          <div style={{ paddingLeft: `${(depth + 1) * 12 + 6}px` }}>
            <TreeSkeleton />
          </div>
        ) : entries.length === 0 ? (
          <p
            className="text-[11px] text-muted-foreground"
            style={{ paddingLeft: `${(depth + 1) * 12 + 24}px` }}
          >
            {isSectionRoot && section
              ? section === "raw"
                ? "(empty — drop a file on Upload to add)"
                : "(empty — populated by ingestion worker)"
              : "(empty)"}
          </p>
        ) : (
          entries.map((e) => (
            <EntryRow
              key={e.path}
              kbId={kbId}
              entry={e}
              openDirs={openDirs}
              entriesByDir={entriesByDir}
              onToggle={onToggle}
              onSelectFile={onSelectFile}
              onDelete={onDelete}
              selectedPath={selectedPath}
              depth={depth + 1}
            />
          ))
        )
      ) : null}
    </div>
  );
}

interface EntryRowProps extends Omit<DirNodeProps, "dir" | "displayName"> {
  entry: KbFileEntry;
}

function EntryRow({
  kbId,
  entry,
  openDirs,
  entriesByDir,
  onToggle,
  onSelectFile,
  onDelete,
  selectedPath,
  depth,
}: EntryRowProps) {
  if (entry.kind === "dir") {
    return (
      <DirNode
        kbId={kbId}
        dir={entry.path}
        displayName={entry.name}
        openDirs={openDirs}
        entriesByDir={entriesByDir}
        onToggle={onToggle}
        onSelectFile={onSelectFile}
        onDelete={onDelete}
        selectedPath={selectedPath}
        depth={depth}
      />
    );
  }
  const isActive = selectedPath === entry.path;
  return (
    <div
      className={`group flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${
        isActive ? "bg-muted" : "hover:bg-muted/60"
      }`}
      style={{ paddingLeft: `${depth * 12 + 18}px` }}
    >
      <button
        type="button"
        className="flex flex-1 items-center gap-1 text-left"
        onClick={() => onSelectFile(entry.path)}
      >
        <FileIcon mime={entry.mime_type} />
        <span className="truncate font-mono">{entry.name}</span>
        <span className="ml-auto text-[10px] text-muted-foreground">
          {formatBytes(entry.size_bytes)}
        </span>
      </button>
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5 opacity-0 group-hover:opacity-100"
        aria-label={`Delete ${entry.name}`}
        onClick={() => onDelete(entry)}
      >
        <Trash2 className="h-3 w-3 text-destructive" />
      </Button>
    </div>
  );
}

function FileIcon({ mime }: { mime: string | null }) {
  if (!mime) return <File className="h-3.5 w-3.5 text-muted-foreground" />;
  if (mime.startsWith("text/") || mime.includes("json") || mime.includes("yaml")) {
    return <FileText className="h-3.5 w-3.5 text-muted-foreground" />;
  }
  return <File className="h-3.5 w-3.5 text-muted-foreground" />;
}

function TreeSkeleton() {
  return (
    <div className="space-y-1.5 py-2">
      <Skeleton className="h-3 w-32" />
      <Skeleton className="h-3 w-28" />
      <Skeleton className="h-3 w-40" />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Preview pane
// ────────────────────────────────────────────────────────────────────

function FilePreview({ kbId, path }: { kbId: string; path: string }) {
  const [head, setHead] = useState<KbHeadResponse | null>(null);
  const [presignUrl, setPresignUrl] = useState<string | null>(null);
  const [meta, setMeta] = useState<KbFileEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setHead(null);
      setPresignUrl(null);
      setMeta(null);
      try {
        const stat = await api<KbFileEntry>(
          `/kbs/${kbId}/fs/stat?${new URLSearchParams({ path }).toString()}`,
        );
        if (cancelled) return;
        setMeta(stat);

        const mime = stat.mime_type ?? "";
        const isText =
          mime.startsWith("text/") || mime.includes("json") || mime.includes("yaml") || mime === "";
        const isImage = mime.startsWith("image/");
        const isPdf = mime === "application/pdf";

        if (isImage || isPdf) {
          // Presign so the browser fetches S3 directly, no proxy through us.
          const presigned = await api<KbCatBytesResponse>(
            `/kbs/${kbId}/fs/cat?${new URLSearchParams({ path, presign: "true" }).toString()}`,
          );
          if (cancelled) return;
          setPresignUrl(presigned.presigned_url);
        } else if (isText) {
          // 32 KiB is enough for a useful glance and well under the API cap.
          const params = new URLSearchParams({ path, n: "32768" });
          const h = await api<KbHeadResponse>(`/kbs/${kbId}/fs/head?${params.toString()}`);
          if (cancelled) return;
          setHead(h);
        }
        // Otherwise fall through to "binary preview unsupported".
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load file");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [kbId, path]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 text-xs">
        <code className="font-mono">{path}</code>
        {meta ? (
          <>
            <Badge variant="outline" className="text-[10px]">
              {formatBytes(meta.size_bytes)}
            </Badge>
            {meta.mime_type ? (
              <Badge variant="outline" className="text-[10px]">
                {meta.mime_type}
              </Badge>
            ) : null}
            <span className="text-muted-foreground">
              modified {new Date(meta.updated_at).toLocaleString()}
            </span>
          </>
        ) : null}
        {meta ? (
          <a
            className="ml-auto text-xs underline underline-offset-2"
            href={apiUrl(`/kbs/${kbId}/fs/cat?${new URLSearchParams({ path }).toString()}`)}
            target="_blank"
            rel="noreferrer"
          >
            download
          </a>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <Skeleton className="h-32" />
        ) : error ? (
          <ErrorBanner>{error}</ErrorBanner>
        ) : head ? (
          <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-foreground">
            {head.body}
            {head.truncated ? (
              <span className="mt-2 block text-[10px] text-muted-foreground">
                — truncated; download for full content —
              </span>
            ) : null}
          </pre>
        ) : presignUrl && meta?.mime_type?.startsWith("image/") ? (
          <PreviewImage src={presignUrl} alt={path} />
        ) : presignUrl && meta?.mime_type === "application/pdf" ? (
          <iframe
            title={path}
            src={presignUrl}
            className="h-[600px] w-full rounded border border-border"
          />
        ) : (
          <EmptyState>
            Binary preview not supported for this MIME type. Use the download link above.
          </EmptyState>
        )}
      </div>
    </div>
  );
}

function PreviewImage({ src, alt }: { src: string; alt: string }) {
  return <img src={src} alt={alt} className="max-h-[600px] rounded border border-border" />;
}

// ────────────────────────────────────────────────────────────────────
// Upload + create KB + mkdir dialogs
// ────────────────────────────────────────────────────────────────────

function UploadButton({
  kbId,
  onUploaded,
}: {
  kbId: string;
  onUploaded: (file: KbFileEntry) => void | Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList) => {
      if (!files.length) return;
      setBusy(true);
      try {
        for (const file of Array.from(files)) {
          // Always upload into raw/ — that's the only writable section.
          // Filename is sanitized so a stray "/" in the original name can't
          // smuggle the upload elsewhere.
          const safeName = (file.name || "upload.bin").replaceAll("/", "_");
          const res = await apiUpload<KbUploadResponse>(`/kbs/${kbId}/fs/upload`, [file], {
            path: `raw/${safeName}`,
          });
          await onUploaded(res.file);
          toast.success(`Uploaded ${res.file.name} → raw/`);
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Upload failed");
      } finally {
        setBusy(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    },
    [kbId, onUploaded],
  );

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) void handleFiles(e.target.files);
  };

  const onDrop = (e: DragEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) void handleFiles(e.dataTransfer.files);
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={dragOver ? "ring-2 ring-primary/40" : undefined}
      >
        {busy ? <Loader2 className="animate-spin" /> : <Upload />}
        {busy ? "Uploading…" : "Upload"}
      </Button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={onChange}
        aria-label="Upload files to knowledge base"
      />
    </>
  );
}

function CreateKbDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (kb: KbView) => void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Auto-derive a slug from the name if the user hasn't typed one.
  useEffect(() => {
    if (!slug) {
      const guess = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      if (guess) setSlug(guess);
    }
  }, [name, slug]);

  async function submit(): Promise<void> {
    if (!name.trim() || !slug.trim()) {
      toast.error("Name and slug are required");
      return;
    }
    setSubmitting(true);
    try {
      const body: CreateTeamKbRequest = {
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() ? description.trim() : null,
      };
      const created = await api<KbView>("/kbs", {
        method: "POST",
        body: JSON.stringify(body),
      });
      await onCreated(created);
      setName("");
      setSlug("");
      setDescription("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create KB");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New knowledge base</DialogTitle>
          <DialogDescription>
            Team-scoped KB. The slug appears in URLs and MCP endpoints — keep it short and URL-safe.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="kb-name">Name</Label>
            <Input
              id="kb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Product Wiki"
              maxLength={120}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kb-slug">Slug</Label>
            <Input
              id="kb-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="e.g. product-wiki"
              maxLength={64}
              pattern="[a-z0-9-]+"
              required
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, digits, and dashes only.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kb-desc">Description</Label>
            <Textarea
              id="kb-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional — what's this KB for?"
              rows={2}
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="animate-spin" /> : null}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MkdirDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (path: string) => void | Promise<void>;
}) {
  const [path, setPath] = useState("raw/");
  const [submitting, setSubmitting] = useState(false);

  async function submit(): Promise<void> {
    if (!path.trim() || path.trim() === "raw/") {
      toast.error("Path is required (e.g. raw/docs)");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(path.trim().replace(/^\/+/, "").replace(/\/+$/, ""));
      setPath("raw/");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New directory</DialogTitle>
          <DialogDescription>
            New directories live under <code>raw/</code>. Use slashes for nested paths (e.g.{" "}
            <code>raw/docs/2026-q2</code>).
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="mkdir-path">Path</Label>
            <Input
              id="mkdir-path"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="raw/docs/2026-q2"
              required
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="animate-spin" /> : null}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// Documents pane — ingest status table + per-row reembed
// ────────────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  // Lifecycle progresses through `pending → ocr → chunked → embedded → indexed`;
  // we lean on the badge variant to convey "in flight" vs "done" vs "broken".
  pending: "secondary",
  ocr: "secondary",
  chunked: "secondary",
  embedded: "default",
  indexed: "default",
  failed: "destructive",
};

function StatusBadge({ status }: { status: DocumentStatus }) {
  const variant = STATUS_VARIANT[status] ?? "outline";
  return (
    <Badge variant={variant} className="text-[10px] capitalize">
      {status}
    </Badge>
  );
}

function DocumentsPane({
  kbId,
  refreshKey,
  onOpenSource,
  onExtractionTriggered,
}: {
  kbId: string;
  refreshKey: number;
  onOpenSource: (uri: string) => void;
  /** Bumped by the parent's ``setExtractionRefreshKey`` so the
   *  Extraction tab refetches its runs / candidate queues right
   *  after the operator clicks "Re-extract". Optional because
   *  earlier callers don't need it. */
  onExtractionTriggered?: () => void;
}) {
  const [docs, setDocs] = useState<KbDocumentView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reembedding, setReembedding] = useState<Set<string>>(new Set());
  const [reextracting, setReextracting] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api<KbDocumentListResponse>(`/kbs/${kbId}/documents?limit=200`);
      setDocs(resp.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [kbId]);

  // ``refreshKey`` is the parent's nudge signal (bumped on upload).
  // We touch it explicitly so biome's exhaustive-deps doesn't flag it
  // as an unused dependency just because the body only calls
  // ``refresh()``.
  useEffect(() => {
    void refreshKey;
    void refresh();
  }, [refresh, refreshKey]);

  // Auto-poll when anything is mid-pipeline. Pure UX: we don't push
  // SSE for ingest status today, so a 5s poll while there's at least
  // one in-flight row is the cheapest "feels live" implementation.
  useEffect(() => {
    if (!docs) return;
    const inFlight = docs.some(
      (d) => d.status === "pending" || d.status === "ocr" || d.status === "chunked",
    );
    if (!inFlight) return;
    const id = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(id);
  }, [docs, refresh]);

  async function handleReembed(doc: KbDocumentView) {
    setReembedding((prev) => new Set(prev).add(doc.id));
    try {
      const resp = await api<KbReembedResponse>(`/kbs/${kbId}/documents/${doc.id}/reembed`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (resp.job_id) {
        toast.success(`Re-embedding queued (job ${resp.job_id})`);
      } else {
        toast.warning("Re-embedding could not be queued — try again in a moment");
      }
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to enqueue re-embedding");
    } finally {
      setReembedding((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  }

  async function handleReextract(doc: KbDocumentView) {
    setReextracting((prev) => new Set(prev).add(doc.id));
    try {
      const resp = await api<KbReextractResponse>(`/kbs/${kbId}/documents/${doc.id}/reextract`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      if (resp.job_id) {
        toast.success(`Extraction queued (job ${resp.job_id})`);
      } else {
        // ``job_id is null`` means knowledge-service couldn't reach
        // Redis; the recovery sweep will retry but the user should
        // know the click didn't take effect immediately.
        toast.warning("Extraction could not be queued — try again in a moment");
      }
      // Bump the Extraction tab's refresh key so the runs table and
      // candidate queues refetch right away — operators get visual
      // confirmation without having to click into the tab and hit
      // refresh.
      onExtractionTriggered?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to enqueue re-extraction");
    } finally {
      setReextracting((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <span className="text-xs text-muted-foreground">
          Ingestion status for documents in this knowledge base.
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto h-6 px-2 text-xs"
          onClick={() => void refresh()}
          aria-label="Refresh documents"
          disabled={loading}
        >
          <RefreshCw className={loading ? "animate-spin" : undefined} />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {error ? (
          <ErrorBanner className="m-3">{error}</ErrorBanner>
        ) : loading && !docs ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-5/6" />
            <Skeleton className="h-6 w-2/3" />
          </div>
        ) : !docs || docs.length === 0 ? (
          <EmptyState className="m-3">
            No documents yet. Upload a file under <code>raw/</code> to start the ingestion pipeline.
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="max-w-[260px]">
                    <button
                      type="button"
                      className="block w-full truncate text-left font-mono text-xs hover:underline"
                      title={d.source_uri}
                      onClick={() => onOpenSource(d.source_uri)}
                    >
                      {d.title || d.source_uri.split("/").pop() || d.id}
                    </button>
                    {d.status_reason ? (
                      <p className="mt-0.5 truncate text-[10px] text-destructive">
                        {d.status_reason}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={d.status} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(d.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs"
                        onClick={() => void handleReembed(d)}
                        disabled={reembedding.has(d.id)}
                      >
                        {reembedding.has(d.id) ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          <RefreshCw />
                        )}
                        Re-embed
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs"
                        onClick={() => void handleReextract(d)}
                        disabled={
                          reextracting.has(d.id) ||
                          d.status === "pending" ||
                          d.status === "ocr" ||
                          d.status === "chunked"
                        }
                        title="Re-run LLM extraction on this document's current version"
                      >
                        {reextracting.has(d.id) ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          <Sparkles />
                        )}
                        Re-extract
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Search pane — embed + retrieve, render ranked snippets
// ────────────────────────────────────────────────────────────────────

function SearchPane({
  kbId,
  onOpenHit,
}: {
  kbId: string;
  onOpenHit: (hit: KbSearchHit) => void;
}) {
  const [query, setQuery] = useState("");
  const [topK, setTopK] = useState(8);
  const [hits, setHits] = useState<KbSearchHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset search state when the active KB changes — stale hits from KB
  // A would be misleading once the user switches to KB B. ``kbId`` is
  // touched (not closed-over inside the setters) so biome sees it as
  // a real dependency rather than complaining about exhaustive-deps.
  useEffect(() => {
    void kbId;
    setQuery("");
    setHits(null);
    setError(null);
  }, [kbId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      const body: KbSearchRequest = { query: trimmed, top_k: topK };
      const resp = await api<KbSearchResponse>(`/kbs/${kbId}/search`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      setHits(resp.hits);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
      setHits(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex h-full flex-col">
      <form
        className="flex items-center gap-2 border-b border-border px-3 py-2"
        onSubmit={handleSubmit}
      >
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask the KB anything…"
          className="h-8 text-xs"
        />
        <Select value={String(topK)} onValueChange={(v) => setTopK(Number(v))}>
          <SelectTrigger className="h-8 w-[70px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[3, 5, 8, 12, 20].map((n) => (
              <SelectItem key={n} value={String(n)}>
                top {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" size="sm" disabled={loading || query.trim().length === 0}>
          {loading ? <Loader2 className="animate-spin" /> : <Sparkles />}
          Search
        </Button>
      </form>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorBanner>{error}</ErrorBanner>
        ) : hits === null ? (
          <EmptyState>
            Type a question above to retrieve relevant chunks. Results are ranked by cosine
            similarity over the team's embedding model.
          </EmptyState>
        ) : hits.length === 0 ? (
          <EmptyState>No matches above the score threshold.</EmptyState>
        ) : (
          <ul className="space-y-2">
            {hits.map((hit) => (
              <li
                key={hit.chunk_id}
                className="rounded-md border border-border bg-card p-2 text-xs"
              >
                <div className="mb-1 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    className="truncate text-left font-mono hover:underline"
                    onClick={() => onOpenHit(hit)}
                    title={hit.source_uri}
                  >
                    {hit.document_title || hit.source_uri.split("/").pop() || hit.chunk_id}
                  </button>
                  <ScorePill score={hit.score} />
                </div>
                <p className="text-[11px] leading-relaxed text-foreground">{hit.snippet}</p>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  seq {hit.seq} · {hit.token_count} tok · {hit.embedding_model}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ScorePill({ score }: { score: number }): ReactNode {
  // Three buckets — high (≥0.8), medium (0.6–0.8), low (<0.6) — keep
  // the chrome minimal so the snippet text dominates. The numeric
  // value is shown so power users can sanity-check the cutoff.
  const label = score.toFixed(3);
  const variant: "default" | "secondary" | "outline" =
    score >= 0.8 ? "default" : score >= 0.6 ? "secondary" : "outline";
  return (
    <Badge variant={variant} className="font-mono text-[10px]">
      {label}
    </Badge>
  );
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

function formatBytes(n: number): string {
  if (n < 1024) return `${n}\u00A0B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}\u00A0KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}\u00A0MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)}\u00A0GB`;
}
